/**
 * Downstream acquisition, disposition, and finance action forms.
 */
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, getDashboardOperator } from "./dashboard-auth.js";

function datetimeLocalToIso(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

export function bindDownstreamAcquisitionActions(statusElement: HTMLElement): void {
  const bindForm = (form: HTMLFormElement, handler: (event: Event) => void): void => {
    if (form.dataset.downstreamBound === "1") return;
    form.dataset.downstreamBound = "1";
    form.addEventListener("submit", handler);
  };
  document.querySelectorAll<HTMLFormElement>("[data-downstream-offer-status-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "offer-sent",
        {
          status: form.querySelector<HTMLSelectElement>("[data-downstream-offer-status]")?.value.trim() || "sent",
          note: form.querySelector<HTMLInputElement>("[data-downstream-offer-note]")?.value.trim() || "Dashboard updated offer lifecycle."
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-title-status-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      const status = form.querySelector<HTMLSelectElement>("[data-downstream-title-status]")?.value.trim() || "clear";
      void submitStructuredLeadAction(
        leadId,
        "title-clear",
        {
          status,
          issueReason: status === "issue_found" ? form.querySelector<HTMLSelectElement>("[data-downstream-title-issue-reason]")?.value.trim() || "other_typed_review" : null,
          note: form.querySelector<HTMLInputElement>("[data-downstream-title-note]")?.value.trim() || "Dashboard updated title review."
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-closing-schedule-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      const closingAt = form.querySelector<HTMLInputElement>("[data-downstream-closing-at]")?.value.trim() || "";
      void submitStructuredLeadAction(
        leadId,
        "closing-schedule",
        {
          closingAt: datetimeLocalToIso(closingAt),
          closingLocation: form.querySelector<HTMLInputElement>("[data-downstream-closing-location]")?.value.trim() || "",
          assignedTo: form.querySelector<HTMLInputElement>("[data-downstream-closing-assigned]")?.value.trim() || getDashboardOperator() || "unassigned",
          note: form.querySelector<HTMLInputElement>("[data-downstream-closing-note]")?.value.trim() || "Dashboard scheduled closing."
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-closing-artifact-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "closing-artifact",
        {
          artifactKind: form.querySelector<HTMLSelectElement>("[data-downstream-closing-artifact-kind]")?.value.trim() || "settlement_statement",
          title: form.querySelector<HTMLInputElement>("[data-downstream-closing-artifact-title]")?.value.trim() || "",
          artifactText: form.querySelector<HTMLInputElement>("[data-downstream-closing-artifact-text]")?.value.trim() || "",
          note: form.querySelector<HTMLInputElement>("[data-downstream-closing-artifact-note]")?.value.trim() || ""
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-asset-onboarding-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "asset-onboarding",
        {
          assignedTo: form.querySelector<HTMLInputElement>("[data-downstream-asset-assigned]")?.value.trim() || getDashboardOperator() || "unassigned",
          note: form.querySelector<HTMLInputElement>("[data-downstream-asset-note]")?.value.trim() || "Dashboard started owned property onboarding."
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-disposition-review-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "disposition-review",
        {
          strategy: form.querySelector<HTMLSelectElement>("[data-downstream-disposition-strategy]")?.value.trim() || "resale",
          status: form.querySelector<HTMLSelectElement>("[data-downstream-disposition-status]")?.value.trim() || "strategy_selected",
          targetPrice: form.querySelector<HTMLInputElement>("[data-downstream-disposition-target-price]")?.value.trim() || null,
          assignedTo: form.querySelector<HTMLInputElement>("[data-downstream-disposition-assigned]")?.value.trim() || getDashboardOperator() || "unassigned",
          note: form.querySelector<HTMLInputElement>("[data-downstream-disposition-note]")?.value.trim() || "Dashboard recorded owned property disposition strategy."
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-disposition-status-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "disposition-disposed",
        {
          status: form.querySelector<HTMLSelectElement>("[data-downstream-disposition-outcome]")?.value.trim() || "disposed",
          targetPrice: form.querySelector<HTMLInputElement>("[data-downstream-disposition-outcome-price]")?.value.trim() || null,
          assignedTo: form.querySelector<HTMLInputElement>("[data-downstream-disposition-outcome-assigned]")?.value.trim() || getDashboardOperator() || "unassigned",
          note: form.querySelector<HTMLInputElement>("[data-downstream-disposition-outcome-note]")?.value.trim() || "Dashboard marked owned property disposition completed."
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-finance-record-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      const saleClosedAt = form.querySelector<HTMLInputElement>("[data-downstream-finance-sale-closed-at]")?.value.trim() || "";
      void submitStructuredLeadAction(
        leadId,
        "finance-record",
        {
          salePrice: form.querySelector<HTMLInputElement>("[data-downstream-finance-sale-price]")?.value.trim() || "",
          saleClosedAt: datetimeLocalToIso(saleClosedAt),
          repairExpenses: form.querySelector<HTMLInputElement>("[data-downstream-finance-repair-expenses]")?.value.trim() || "",
          holdingExpenses: form.querySelector<HTMLInputElement>("[data-downstream-finance-holding-expenses]")?.value.trim() || "",
          sellingExpenses: form.querySelector<HTMLInputElement>("[data-downstream-finance-selling-expenses]")?.value.trim() || "",
          otherExpenses: form.querySelector<HTMLInputElement>("[data-downstream-finance-other-expenses]")?.value.trim() || "",
          note: form.querySelector<HTMLInputElement>("[data-downstream-finance-note]")?.value.trim() || ""
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-finance-artifact-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "finance-artifact",
        {
          artifactKind: form.querySelector<HTMLSelectElement>("[data-downstream-finance-artifact-kind]")?.value.trim() || "sale_settlement_statement",
          title: form.querySelector<HTMLInputElement>("[data-downstream-finance-artifact-title]")?.value.trim() || "",
          artifactText: form.querySelector<HTMLInputElement>("[data-downstream-finance-artifact-text]")?.value.trim() || "",
          note: form.querySelector<HTMLInputElement>("[data-downstream-finance-artifact-note]")?.value.trim() || ""
        },
        statusElement
      );
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-downstream-finance-reconciliation-form]").forEach((form) => {
    bindForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredLeadAction(
        leadId,
        "finance-reconciliation",
        {
          reconciliationSource: form.querySelector<HTMLSelectElement>("[data-downstream-finance-reconciliation-source]")?.value.trim() || "bank_deposit",
          expectedAmount: form.querySelector<HTMLInputElement>("[data-downstream-finance-reconciliation-expected]")?.value.trim() || "",
          observedAmount: form.querySelector<HTMLInputElement>("[data-downstream-finance-reconciliation-observed]")?.value.trim() || "",
          toleranceAmount: form.querySelector<HTMLInputElement>("[data-downstream-finance-reconciliation-tolerance]")?.value.trim() || "",
          reference: form.querySelector<HTMLInputElement>("[data-downstream-finance-reconciliation-reference]")?.value.trim() || "",
          note: form.querySelector<HTMLInputElement>("[data-downstream-finance-reconciliation-note]")?.value.trim() || ""
        },
        statusElement
      );
    });
  });
}

export function leadActionUrl(leadId: string, action: string): string | null {
  const endpointByAction: Record<string, string> = {
    "offer-sent": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/offer-status`,
    "title-clear": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/title-status`,
    "closing-schedule": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/closing-schedule`,
    "closing-artifact": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/closing-artifact`,
    "asset-onboarding": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/asset-onboarding`,
    "disposition-review": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/disposition-review`,
    "disposition-disposed": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/disposition-status`,
    "finance-record": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/finance-record`,
    "finance-artifact": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/finance-artifact`,
    "finance-reconciliation": `/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/finance-reconciliation`
  };
  return endpointByAction[action] ?? null;
}

export async function submitStructuredLeadAction(
  leadId: string,
  action: string,
  payload: Record<string, string | null>,
  statusElement: HTMLElement
): Promise<void> {
  const url = leadActionUrl(leadId, action);
  if (!url) return;
  statusElement.textContent = `${labelize(action)} ${leadId}...`;
  try {
    const response = await authenticatedFetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: ${labelize(action)} recorded.`;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Lead action failed.";
  }
}

